import React, { useState } from "react";
import "./MultiFileUpload.css";
import {
  Upload,
  Rejectedicon,
} from "../../../../../Components/IconComponents";
import PreviewFile from "../../Form/PreviewFile/PreviewFile";
import { validFileTypes } from "../FileUpload/fileUploadJson";
import { useTranslation } from "react-i18next";
import { VideoIcon, FileText } from "lucide-react";

// Images, PDFs and Word docs — matches the yup validation in CreateTaskConfig.js
const supportedTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const maxFileSizeBytes = 5 * 1024 * 1024; // 5MB per file

function MultiFileUploadForTask({
  label,
  name,
  errors,
  register,
  mandatory,
  handleFileChange,
  uploadfilewidth,
  maxFiles = 3, // total allowed, including files already attached to the record
  existingFileCount = 0,
}) {
  const allowedNewFiles = Math.max(maxFiles - existingFileCount, 0);
  const [fileObjs, setFileObjs] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [maxFilesError, setMaxFilesError] = useState(false); // Add this state
  const [typeError, setTypeError] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const { t } = useTranslation();

  const { onChange, ...registerParams } = register(name);

  const isImage = (fileType) => fileType.startsWith('image/');
  const isVideo = (fileType) => fileType.startsWith('video/');

  const handleFileChangeInternal = (event) => {
    const newFiles = Array.from(event.target.files);

    // Check if adding new files would exceed max files limit
    if (fileObjs.length >= allowedNewFiles) {
      setMaxFilesError(true);
      // Clear the error after 3 seconds
      setTimeout(() => setMaxFilesError(false), 3000);
      return; // Don't process any files
    }

    let hasInvalidType = false;
    let hasOversizedFile = false;
    let hitMaxFiles = false;
    const filteredFiles = [];

    for (const file of newFiles) {
      if (fileObjs.length + filteredFiles.length >= allowedNewFiles) {
        hitMaxFiles = true;
        break;
      }
      if (!supportedTypes.includes(file.type)) {
        hasInvalidType = true;
        continue;
      }
      if (file.size > maxFileSizeBytes) {
        hasOversizedFile = true;
        continue;
      }
      const isNotDuplicate =
        !fileObjs.some((f) => f.name === file.name) &&
        !filteredFiles.some((f) => f.name === file.name);
      if (!isNotDuplicate) continue;

      filteredFiles.push(file);
    }

    setTypeError(hasInvalidType);
    setSizeError(hasOversizedFile);
    if (hasInvalidType) setTimeout(() => setTypeError(false), 4000);
    if (hasOversizedFile) setTimeout(() => setSizeError(false), 4000);

    if (hitMaxFiles) {
      setMaxFilesError(true);
      setTimeout(() => setMaxFilesError(false), 3000);
    }

    if (filteredFiles.length === 0) return;

    // Update local state
    const updatedFiles = [...fileObjs, ...filteredFiles];
    setFileObjs(updatedFiles);

    // Notify parent component
    if (handleFileChange) {
      handleFileChange({
        target: {
          name,
          files: updatedFiles
        }
      });
    }

    // Notify React Hook Form
    if (onChange) {
      onChange({
        target: {
          name,
          value: updatedFiles
        }
      });
    }
  };

  const previewHandler = (file) => {
    setPreviewFile(file);
    setShowPreview(true);
  };

  const removeFile = (fileIndex) => {
    const newFiles = fileObjs.filter((_, index) => index !== fileIndex);
    setFileObjs(newFiles);
    setMaxFilesError(false); // Clear errors when a file is removed
    setTypeError(false);
    setSizeError(false);

    // Notify parent component
    if (handleFileChange) {
      handleFileChange({
        target: {
          name,
          files: newFiles
        }
      });
    }

    // Notify React Hook Form
    if (onChange) {
      onChange({
        target: {
          name,
          value: newFiles
        }
      });
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById(name);
    if (fileInput) fileInput.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const dt = event.dataTransfer;
    if (dt.files && dt.files.length) {
      handleFileChangeInternal({ target: { files: dt.files } });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  return (
    <div className="sales__upload">
      <label className="form__label">
        {t(label)}
        {mandatory && <span className="asterisk">*</span>}
      </label>

      <div className="multifile-all_card_view">
        {/* File preview thumbnails */}
        {fileObjs.map((file, index) => (
          <div
            key={`${file.name}-${index}`}
            style={{
              position: 'relative',
              width: '120px',
              margin: '8px'
            }}
          >
            <div style={{
              position: 'relative',
              width: '100%',
              height: '120px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <div
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  zIndex: 1,
                  borderRadius: '50%',
                  padding: '2px',
                  boxShadow: '0 1px 3px rgba(56, 54, 54, 0.2)',
                  cursor: 'pointer'
                }}
                onClick={() => removeFile(index)}
              >
                <Rejectedicon width="1rem" height="1rem" />
              </div>
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={() => previewHandler(file)}
                >
                  {isImage(file.type) ? (
                    <img
                      src={URL.createObjectURL(file)}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      alt={file.name}
                    />
                  ) : isVideo(file.type) ? (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#1e2127ff'
                    }}>
                      <VideoIcon width="2rem" height="2rem" />
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#eef2f7'
                    }}>
                      <FileText width="2rem" height="2rem" color="#c0392b" />
                    </div>
                  )}
                </div>
                <div style={{
                  marginTop: '4px',
                  width: '100%',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <span style={{
                    fontSize: '12px',
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    textAlign: 'center',
                    width: '100%',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {file.name}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* File preview modal */}
        {!uploadfilewidth && showPreview && (
          <PreviewFile
            file={previewFile}
            show={showPreview}
            previewHandler={() => setShowPreview(false)}
            title="File Preview"
          />
        )}

        {/* Upload area */}
        <div
          className="multi_file_upload cursor-pointer"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={triggerFileInput}
          style={{ width: uploadfilewidth ? '100%' : 'auto' }}
        >
          <div className="upload-content">
            <div className="icon__size--multifile">
              <Upload />
            </div>
            <div className="multi-file_text">
              {uploadfilewidth ? t("Upload files (JPEG, PNG, MP4)") : t("Drag and drop here or browse")}
            </div>
            {!uploadfilewidth && (
              <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                {t("Supports: JPEG, PNG, GIF, WEBP, PDF, DOC, DOCX")}
              </div>
            )}
          </div>

          <input
            id={name}
            type="file"
            name={name}
            style={{ display: "none" }}
            onChange={handleFileChangeInternal}
            multiple
            accept={uploadfilewidth ? "image/*,video/*" : supportedTypes.join(",")}
            {...registerParams}
          />
        </div>
      </div>

      {/* Validation messages */}
      {maxFilesError && (
        <span className="error__message">
          {t(`You can upload up to ${maxFiles} file${maxFiles > 1 ? 's' : ''} max.`)}
        </span>
      )}
      {typeError && (
        <span className="error__message">
          {t("Invalid file type. Allowed formats: JPEG, PNG, GIF, WEBP, PDF, DOC, DOCX.")}
        </span>
      )}
      {sizeError && (
        <span className="error__message">
          {t("Each file must be 5MB or smaller.")}
        </span>
      )}
      {errors[name]?.message && (
        <span className="error__message">{errors[name]?.message}</span>
      )}
    </div>
  );
}

export default MultiFileUploadForTask;